import { Injectable } from "@angular/core";

declare const pdfjsLib: {
  PDFWorker: new () => PdfJsWorker;
};

export type PdfJsWorker = {
  promise: Promise<void>;
};

/**
 * Single pdf.js worker shared by the whole app.
 *
 * Pass it explicitly to `pdfjsLib.getDocument({ url, worker })`:
 * - a worker passed explicitly is not owned by the loading task, so
 *   `pdfDocument.destroy()` frees the document without terminating the worker;
 * - do not publish it through `GlobalWorkerOptions.workerPort`:
 *   `PDFWorker.fromPort()` does not recognise the port of a `new PDFWorker()`
 *   and attaches a second "main" MessageHandler to the same Worker, which then
 *   throws "Cannot resolve callback N" on every response (SNT-12449).
 */
@Injectable({
  providedIn: "root"
})
export class PdfJsWorkerService {
  #workerPromise: Promise<PdfJsWorker> | null = null;

  getWorker(): Promise<PdfJsWorker> {
    if (!this.#workerPromise) {
      this.#workerPromise = this.#createWorker().catch((error: unknown) => {
        this.#workerPromise = null;

        throw error;
      });
    }

    return this.#workerPromise;
  }

  async #createWorker(): Promise<PdfJsWorker> {
    await this.#waitForPdfJsLib();

    const worker = new pdfjsLib.PDFWorker();
    await worker.promise;

    return worker;
  }

  #waitForPdfJsLib(): Promise<void> {
    if (typeof pdfjsLib !== "undefined") {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const intervalId = window.setInterval(() => {
        if (typeof pdfjsLib !== "undefined") {
          window.clearInterval(intervalId);
          resolve();
        }
      }, 50);
    });
  }
}
